import React, { FC, useCallback, useMemo } from 'react';
import { FocusedFieldProps } from '../../types';
import { Controller, useFormContext } from 'react-hook-form';
import { FORM_FIELDS, LABELS, ORGANIZATION_CHOICE_TYPE } from '../../../../../constants';
import { SelectInput } from '../../../../landing.common';
import {
    AREA_OF_EMPLOYMENT_SOURCE,
    EMPLOYER_TYPE_SOURCES,
    EMPLOYMENT_TYPE_SOURCE,
    EMPLOYMENT_VALUES,
    JOB_TYPE_SOURCE,
} from '../../../../../constants/sources';
import { OrganizationNameField, SalaryField } from '../../../steps/work/components';
import {
    AnotherUnemploymentReasonField,
    AreaOfEmploymentField,
    EmployerInnField,
    EmployerTypeField,
    JobTitleField,
    JobTypeField,
    UnEmploymentReasonField,
    WorkAddressField,
    WorkPhoneField,
} from '../../../../../helpers/form.new/fields/work';
import { RadioField } from '../../components/form/components/focused.sheet';
import { LastExperienceMobileField } from './components/mobile';
import { LastExperienceFields } from './components/desktop';
import { namesOfOrganizationFields, WPViews } from '../../../steps/work/constants';
import { extendFbPayload, extendFormStore, updateWorkStepOptions } from '../../../../../store/reducers/formStore';
import { useDispatch } from 'react-redux';
import { FocusedFieldWithAlert } from '../common';

export const EmploymentTypeField: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const employmentTypeOptions = useMemo(
        () =>
            EMPLOYMENT_TYPE_SOURCE.filter((item) => item.value !== EMPLOYMENT_VALUES.JUDGE).map(
                ({ name: label, value }) => ({ label, value }),
            ),
        [],
    );

    if (isShowingOnSheet) {
        return <RadioField options={employmentTypeOptions} fieldName={FORM_FIELDS.EMPLOYMENT_TYPE} form={form} />;
    }

    return (
        <Controller
            control={control}
            name={FORM_FIELDS.EMPLOYMENT_TYPE}
            render={({ field }) => (
                <SelectInput
                    {...field}
                    withCrossIcon={false}
                    options={employmentTypeOptions}
                    label={LABELS[FORM_FIELDS.EMPLOYMENT_TYPE]}
                    errorMessage={errors[FORM_FIELDS.EMPLOYMENT_TYPE]?.message}
                    {...props}
                />
            )}
        />
    );
};

// TODO: после полного перехода на новую анкету, перенести реализацию нижеперечисленных полей в этот файл

export const OrganizationNameFieldContainer: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const { setValue, watch, clearErrors } = form;
    const dispatch = useDispatch();
    const workFieldsValues = watch(namesOfOrganizationFields);
    const stringifiedValues = workFieldsValues.join(' ');

    const openWorkPlaceEditorManual = useCallback(() => {
        dispatch(updateWorkStepOptions({ workPlaceView: WPViews.wpVisibleManual }));
    }, [dispatch]);

    const clearFields = useCallback(
        (fields: ReadonlyArray<string>) => {
            clearErrors(fields);
            fields.forEach((fieldName) => setValue(fieldName, '', { shouldValidate: false }));
        },
        [clearErrors, setValue],
    );

    const onOrganizationChange = useCallback(
        (event: FE.FieldChangeEvent) => {
            const areAllWorkPlaceFieldsFilled = workFieldsValues.every((val) => Boolean(val));

            if (areAllWorkPlaceFieldsFilled || !event.target.value) {
                clearFields(
                    namesOfOrganizationFields.filter((fieldName) => fieldName !== FORM_FIELDS.ORGANIZATION_NAME),
                );
            }
        },
        [clearFields, stringifiedValues],
    );

    const onOrganizationClear = useCallback(() => {
        clearFields(namesOfOrganizationFields);
    }, [clearFields]);

    const onOrganizationBlur: React.ComponentProps<typeof OrganizationNameField>['onBlur'] = useCallback(
        (event, onBlur, option?: Dadata.OrganizationSuggestItem) => {
            if (option) {
                dispatch(extendFormStore({ organizationChoiceType: ORGANIZATION_CHOICE_TYPE.SUGGEST }));
                dispatch(extendFbPayload({ work_organization_status: option.data.state.status || undefined }));
            } else {
                dispatch(extendFbPayload({ work_organization_status: undefined }));
                dispatch(extendFormStore({ organizationChoiceType: ORGANIZATION_CHOICE_TYPE.BY_YOURSELF }));
            }

            onBlur(event);
        },
        [dispatch],
    );

    return (
        <OrganizationNameField
            form={form}
            onChange={onOrganizationChange}
            onBlur={onOrganizationBlur}
            onClear={onOrganizationClear}
            onManual={openWorkPlaceEditorManual}
            {...props}
        />
    );
};

export const WorkPhoneNumberFieldContainer: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return <WorkPhoneField control={control} error={errors[FORM_FIELDS.WORK_PHONE]?.message} {...props} />;
};

export const UnEmploymentReasonFieldContainer: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <UnEmploymentReasonField
            control={control}
            error={errors[FORM_FIELDS.UNEMPLOYMENT_REASON]?.message}
            {...props}
        />
    );
};

export const AnotherUnemploymentReasonFieldContainer: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <AnotherUnemploymentReasonField
            control={control}
            error={errors[FORM_FIELDS.ANOTHER_UNEMPLOYMENT_REASON]?.message}
            {...props}
        />
    );
};

export const JobTitleFieldContainer: FC<FocusedFieldProps> = (props) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return <JobTitleField control={control} error={errors[FORM_FIELDS.JOB_TITLE]?.message} {...props} />;
};

export const JobTypeFieldContainer: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const jobTypeOptions = useMemo(() => JOB_TYPE_SOURCE.map(({ name: label, value }) => ({ label, value })), []);

    if (isShowingOnSheet) {
        return <RadioField options={jobTypeOptions} fieldName={FORM_FIELDS.JOB_TYPE} form={form} />;
    }

    return <JobTypeField control={control} error={errors[FORM_FIELDS.JOB_TYPE]?.message} {...props} />;
};

export const SalaryFieldContainer: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();

    return (
        <FocusedFieldWithAlert
            field={<SalaryField form={form} showFocusTooltip={!isShowingOnSheet} {...props} />}
            showAlert={isShowingOnSheet}
            alert="Укажите ваш суммарный доход после вычета налогов"
        />
    );
};

export const LastExperienceFieldsContainer: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();

    if (isShowingOnSheet) {
        return <LastExperienceMobileField form={form} {...props} />;
    }

    return <LastExperienceFields form={form} {...props} />;
};

export const EmployerTypeFieldContainer: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const employerTypeOptions = useMemo(
        () => EMPLOYER_TYPE_SOURCES.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    if (isShowingOnSheet) {
        return <RadioField options={employerTypeOptions} fieldName={FORM_FIELDS.EMPLOYER_TYPE} form={form} />;
    }

    return <EmployerTypeField control={control} error={errors[FORM_FIELDS.EMPLOYER_TYPE]?.message} {...props} />;
};

export const AreaOfEmploymentFieldContainer: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    const areaOfEmploymentOptions = useMemo(
        () => AREA_OF_EMPLOYMENT_SOURCE.map(({ name: label, value }) => ({ label, value })),
        [],
    );

    if (isShowingOnSheet) {
        return <RadioField options={areaOfEmploymentOptions} fieldName={FORM_FIELDS.AREA_OF_EMPLOYMENT} form={form} />;
    }

    return (
        <AreaOfEmploymentField control={control} error={errors[FORM_FIELDS.AREA_OF_EMPLOYMENT]?.message} {...props} />
    );
};

export const EmployerInnFieldContainer: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return (
        <EmployerInnField
            control={control}
            error={errors[FORM_FIELDS.EMPLOYER_INN]?.message}
            additionalMessage={undefined}
            {...props}
        />
    );
};

export const WorkAddressFieldContainer: FC<FocusedFieldProps> = ({ isShowingOnSheet, ...props }) => {
    const form = useFormContext();
    const {
        control,
        formState: { errors, dirtyFields: _ },
    } = form;

    return <WorkAddressField control={control} error={errors[FORM_FIELDS.WORK_ADDRESS]?.message} {...props} />;
};
